import { computed, watch } from "vue";
import { useAuthStore } from "~/stores/useAuthStore";
import {
  BASE_DAYCARE_SLOTS,
  MAX_EXTRA_DAYCARE_SLOTS,
  useDaycareStore,
} from "~/stores/useDaycareStore";
import { usePlayerStore } from "~/stores/usePlayerStore";
import { useInvocationLockStore } from "~/stores/useInvocationLockStore";
import { applyPerSlotLoopRefillsFromHatch } from "~/composables/useDaycareSlotLoopRefill";
import { applyDaycareInventoryRefill } from "~/composables/useDaycareInventoryRefill";
import { hatchDaycareOnServer } from "~/composables/useDaycareSync";
import { useDaycareHatchToasts } from "~/composables/useDaycareHatchToasts";
import { useAudioManager } from "~/composables/useAudioManager";

const NOTIF_PREFS_KEY = "poke-idle-notif-prefs";

function isNotifEnabled(key: string): boolean {
  try {
    const raw =
      typeof localStorage !== "undefined"
        ? localStorage.getItem(NOTIF_PREFS_KEY)
        : null;
    if (!raw) return true;
    const prefs = JSON.parse(raw) as Record<string, boolean>;
    return prefs[key] ?? true;
  } catch {
    return true;
  }
}

/** Verrou partagé avec le remplissage « slot libre » dans `game.vue`. */
let daycareInventoryBusy = false;

export function setDaycareInventoryBusy(value: boolean) {
  daycareInventoryBusy = value;
}

export function isDaycareInventoryBusy(): boolean {
  return daycareInventoryBusy;
}

let autoRefillInFlight: Promise<void> | null = null;
let autoRefillRetryTimer: ReturnType<typeof setTimeout> | null = null;
let autoRefillWatchdogInterval: ReturnType<typeof setInterval> | null = null;
let autoRefillWatcherStops: (() => void)[] = [];
/** Backoff après hatch vide / échec — évite la tempête PUT+POST qui sature le rate-limit. */
let autoRefillBackoffUntil = 0;
let autoRefillFailStreak = 0;

const AUTO_REFILL_RETRY_MS = 150;
const AUTO_REFILL_WATCHDOG_MS = 12_000;
const AUTO_REFILL_BACKOFF_BASE_MS = 2_000;
const AUTO_REFILL_BACKOFF_MAX_MS = 60_000;

function scheduleAutoRefillRetry(delayMs = AUTO_REFILL_RETRY_MS) {
  if (autoRefillRetryTimer) return;
  autoRefillRetryTimer = setTimeout(() => {
    autoRefillRetryTimer = null;
    void runDaycareAutoRefill();
  }, Math.max(0, delayMs));
}

function noteAutoRefillFailure() {
  autoRefillFailStreak = Math.min(8, autoRefillFailStreak + 1);
  const delay = Math.min(
    AUTO_REFILL_BACKOFF_MAX_MS,
    AUTO_REFILL_BACKOFF_BASE_MS * 2 ** (autoRefillFailStreak - 1),
  );
  autoRefillBackoffUntil = Date.now() + delay;
  scheduleAutoRefillRetry(delay);
}

function noteAutoRefillSuccess() {
  autoRefillFailStreak = 0;
  autoRefillBackoffUntil = 0;
}

/** Collecte les œufs prêts, applique boucle / remplissage auto, sync serveur. */
export async function runDaycareAutoRefill(): Promise<void> {
  const daycare = useDaycareStore();
  if (!daycare.autoRefill) return;
  if (daycare.slots.every((s) => s.damageDealt < s.damageRequired)) return;
  if (Date.now() < autoRefillBackoffUntil) {
    scheduleAutoRefillRetry(autoRefillBackoffUntil - Date.now());
    return;
  }

  if (daycareInventoryBusy) {
    scheduleAutoRefillRetry();
    return;
  }
  if (autoRefillInFlight) return autoRefillInFlight;

  autoRefillInFlight = (async () => {
    daycareInventoryBusy = true;
    try {
      const auth = useAuthStore();
      const daycare = useDaycareStore();
      const player = usePlayerStore();
      const invocationLock = useInvocationLockStore();
      const audio = useAudioManager();
      const { notifyShinyHatched } = useDaycareHatchToasts();

      daycare.passiveVacancyRefillSuppressed = false;
      const result = await hatchDaycareOnServer();
      if (!result || result.hatched.length === 0) {
        // Œufs toujours prêts côté client → ne pas marteler sync/hatch.
        if (daycare.slots.some((s) => s.damageDealt >= s.damageRequired)) {
          noteAutoRefillFailure();
        }
        return;
      }

      noteAutoRefillSuccess();
      let hasShinyHatch = false;
      for (const { slot, isShiny } of result.hatched) {
        if (isShiny) {
          hasShinyHatch = true;
          if (isNotifEnabled("daycare")) {
            notifyShinyHatched(slot.nameFr, slot.nameEn);
          }
        }
      }
      if (hasShinyHatch) {
        audio.playSfx("shinyEncounter");
      }
      const maxSlots =
        BASE_DAYCARE_SLOTS +
        Math.min(MAX_EXTRA_DAYCARE_SLOTS, player.extraDaycareSlots ?? 0);
      applyPerSlotLoopRefillsFromHatch(result.hatched, maxSlots);
      if (daycare.autoVacancyFill) {
        applyDaycareInventoryRefill(maxSlots);
      }
      if (auth.gameLoaded) {
        const { syncDaycareToServer } =
          await import("~/composables/useDaycareSync");
        if (invocationLock.active) {
          void syncDaycareToServer(true);
        } else {
          await syncDaycareToServer(true);
        }
      }
    } finally {
      daycareInventoryBusy = false;
    }
  })();

  try {
    await autoRefillInFlight;
  } finally {
    autoRefillInFlight = null;
  }
}

function readyEggsCount(): number {
  const daycare = useDaycareStore();
  return daycare.slots.filter((s) => s.damageDealt >= s.damageRequired).length;
}

/** Relance la collecte auto si des œufs restent bloqués en « prêt » (file d’attente, onglet, course). */
export function nudgeDaycareAutoRefillIfNeeded() {
  const daycare = useDaycareStore();
  if (!daycare.autoRefill) return;
  if (Date.now() < autoRefillBackoffUntil) return;
  if (readyEggsCount() > 0) void runDaycareAutoRefill();
}

export function bindDaycareAutoRefillWatchers(
  getGameplayBootstrapped: () => boolean,
) {
  const auth = useAuthStore();
  const daycare = useDaycareStore();
  const { queueReadyEggsToast } = useDaycareHatchToasts();

  const readyEggs = computed(() => readyEggsCount());

  autoRefillWatcherStops.push(
    watch(
      readyEggs,
      async (newVal, oldVal) => {
        if (!auth.gameLoaded) return;
        if (daycare.autoRefill && newVal > 0) {
          await runDaycareAutoRefill();
          return;
        }
        if (
          !daycare.autoRefill &&
          oldVal !== undefined &&
          newVal > oldVal &&
          isNotifEnabled("daycare")
        ) {
          queueReadyEggsToast(newVal - oldVal);
        }
      },
      { flush: "post", immediate: true },
    ),
    watch(
      () => daycare.autoRefill,
      async (enabled) => {
        if (!auth.gameLoaded) return;
        if (enabled && readyEggs.value > 0) {
          await runDaycareAutoRefill();
        }
      },
    ),
    watch(
      () =>
        [
          auth.gameLoaded,
          getGameplayBootstrapped(),
          daycare.autoRefill,
          readyEggs.value,
        ] as const,
      ([loaded, bootstrapped, autoRefill, ready]) => {
        if (!loaded || !bootstrapped || !autoRefill || ready <= 0) return;
        void runDaycareAutoRefill();
      },
      { flush: "post" },
    ),
  );

  if (!autoRefillWatchdogInterval && typeof window !== "undefined") {
    autoRefillWatchdogInterval = setInterval(() => {
      if (!auth.gameLoaded || !getGameplayBootstrapped()) return;
      nudgeDaycareAutoRefillIfNeeded();
    }, AUTO_REFILL_WATCHDOG_MS);
  }
}

export function unbindDaycareAutoRefillWatchers() {
  for (const stop of autoRefillWatcherStops) stop();
  autoRefillWatcherStops = [];
  if (autoRefillRetryTimer) {
    clearTimeout(autoRefillRetryTimer);
    autoRefillRetryTimer = null;
  }
  if (autoRefillWatchdogInterval) {
    clearInterval(autoRefillWatchdogInterval);
    autoRefillWatchdogInterval = null;
  }
  autoRefillFailStreak = 0;
  autoRefillBackoffUntil = 0;
}
